import { ArrowLeftIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Flex, HStack, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { PaginationButton } from '@/components/Pagination/PaginationButton';

import { PaginationProps } from '@/models/props/PaginationProps';

function Pagination(props: PaginationProps) {
    const { pageCount, page } = props;
    const router = useRouter();
    const [range, setRange] = useState([0, 3]);

    useEffect(() => {
        switch (page) {
            case 1:
                setRange([0, 3]);
                break;
            case pageCount:
                setRange([pageCount - 3 < 0 ? 0 : pageCount - 3, pageCount]);
                break;
            default:
                setRange([page - 2, page + 1]);
                break;
        }
    }, [page, pageCount]);

    const changePage = useCallback(
        async (page?: number | null) => {
            if (!page) {
                return;
            }
            const url = new URL(location.href);
            const queryParams = url.searchParams;
            page > 1 ? queryParams.set('page', `${page}`) : queryParams.delete('page');
            await router.push(url.toString());
        },
        [router]
    );

    return (
        <>
            {pageCount > 1 && (
                <Flex justifyContent="end" pt={6}>
                    <HStack spacing={2}>
                        {page > 1 && (
                            <>
                                <PaginationButton
                                    onClick={(page) => changePage(page)}
                                    page={1}
                                    disabled={page <= 1}
                                >
                                    <ArrowLeftIcon pointerEvents="none" height={2} />
                                </PaginationButton>
                                <PaginationButton
                                    onClick={(page) => changePage(page)}
                                    page={page - 1}
                                    disabled={page <= 1}
                                >
                                    <ChevronLeftIcon pointerEvents="none" />
                                </PaginationButton>
                            </>
                        )}
                        {pageCount > 3 && (
                            <>
                                {[...Array(pageCount).keys()]
                                    .slice(range[0], range[1])
                                    .map((num, i) => (
                                        <PaginationButton
                                            onClick={(page) => changePage(page)}
                                            key={i}
                                            page={num + 1}
                                            disabled={page === num + 1}
                                            styleProps={{
                                                bgColor: page === num + 1 ? 'blue.700' : undefined
                                            }}
                                        >
                                            <Text as="span">{num + 1}</Text>
                                        </PaginationButton>
                                    ))}
                                {page !== pageCount && (
                                    <PaginationButton
                                        onClick={(page) => changePage(page)}
                                        styleProps={{
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <Text as="span">...</Text>
                                    </PaginationButton>
                                )}
                            </>
                        )}
                        {page < pageCount && (
                            <>
                                <PaginationButton
                                    onClick={(page) => changePage(page)}
                                    page={page + 1}
                                    disabled={page >= pageCount}
                                >
                                    <ChevronRightIcon pointerEvents="none" />
                                </PaginationButton>
                                <PaginationButton
                                    onClick={(page) => changePage(page)}
                                    page={pageCount}
                                    disabled={page >= pageCount}
                                >
                                    <ArrowRightIcon pointerEvents="none" height={2} />
                                </PaginationButton>
                            </>
                        )}
                    </HStack>
                </Flex>
            )}
        </>
    );
}

export default Pagination;
